import React, { useEffect, useRef, useState } from "react";
import { mutate } from "swr";
import useCompanyInfo, { type CompanyAddress } from "@/components/context/useCompanyInfo";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { COUNTRIES } from "@/lib/countries";
import { TAX_ID_TYPES } from "@/lib/taxIdTypes";

const EMPTY_ADDRESS: CompanyAddress = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
};

export default function CompanyInfoCard(): React.JSX.Element {
  const { companyInfo, isLoading } = useCompanyInfo();

  const [name, setName] = useState("");
  const [address, setAddress] = useState<CompanyAddress>(EMPTY_ADDRESS);
  const [taxIdType, setTaxIdType] = useState<string>(TAX_ID_TYPES[0].code);
  const [taxIdValue, setTaxIdValue] = useState("");

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Populate the form from SWR once. Later revalidations do not clobber
  // in-progress edits; external changes (rare) require a page reload.
  const initialized = useRef(false);

  useEffect(() => {
    if (!companyInfo || initialized.current) return;
    setName(companyInfo.name ?? "");
    setAddress({
      line1: companyInfo.address?.line1 ?? "",
      line2: companyInfo.address?.line2 ?? "",
      city: companyInfo.address?.city ?? "",
      state: companyInfo.address?.state ?? "",
      postal_code: companyInfo.address?.postal_code ?? "",
      country: companyInfo.address?.country ?? "",
    });
    if (companyInfo.tax_id) {
      setTaxIdType(companyInfo.tax_id.type);
      setTaxIdValue(companyInfo.tax_id.value);
    }
    initialized.current = true;
  }, [companyInfo]);

  // Clear the "Saved." message once the user starts editing again.
  useEffect(() => {
    setSavedAt(null);
  }, [name, address, taxIdType, taxIdValue]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setErrorMsg(null);

    const body = {
      name: name.trim(),
      address,
      tax_id: taxIdValue.trim() ? { type: taxIdType, value: taxIdValue.trim() } : null,
    };

    try {
      const resp = await fetch("/web_api/billing/company_info", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok || data.status !== "ok") {
        setErrorMsg(data.message || "Could not save. Try again.");
      } else {
        setSavedAt(Date.now());
        mutate("/web_api/billing/company_info");
      }
    } catch {
      setErrorMsg("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company information</CardTitle>
        <CardDescription>Your invoices will show this information.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <form onSubmit={onSave} className="space-y-4">
            <div>
              <label htmlFor="ci-name" className="text-sm font-medium">
                Company name
              </label>
              <input
                id="ci-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="ci-line1" className="text-sm font-medium">
                  Address line 1
                </label>
                <input
                  id="ci-line1"
                  type="text"
                  value={address.line1 ?? ""}
                  onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="ci-line2" className="text-sm font-medium">
                  Address line 2
                </label>
                <input
                  id="ci-line2"
                  type="text"
                  value={address.line2 ?? ""}
                  onChange={(e) => setAddress({ ...address, line2: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="ci-city" className="text-sm font-medium">
                  City
                </label>
                <input
                  id="ci-city"
                  type="text"
                  value={address.city ?? ""}
                  onChange={(e) => setAddress({ ...address, city: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="ci-state" className="text-sm font-medium">
                  State / Province
                </label>
                <input
                  id="ci-state"
                  type="text"
                  value={address.state ?? ""}
                  onChange={(e) => setAddress({ ...address, state: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="ci-postal" className="text-sm font-medium">
                  Postal code
                </label>
                <input
                  id="ci-postal"
                  type="text"
                  value={address.postal_code ?? ""}
                  onChange={(e) => setAddress({ ...address, postal_code: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="ci-country" className="text-sm font-medium">
                  Country
                </label>
                <select
                  id="ci-country"
                  value={address.country ?? ""}
                  onChange={(e) => setAddress({ ...address, country: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer"
                >
                  <option value="">—</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="ci-tax-type" className="text-sm font-medium">
                  Tax ID type
                </label>
                <select
                  id="ci-tax-type"
                  value={taxIdType}
                  onChange={(e) => setTaxIdType(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer"
                >
                  {TAX_ID_TYPES.map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="ci-tax-value" className="text-sm font-medium">
                  Tax ID value
                </label>
                <input
                  id="ci-tax-value"
                  type="text"
                  value={taxIdValue}
                  onChange={(e) => setTaxIdValue(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>

            {errorMsg && (
              <p role="alert" className="text-sm text-red-600">
                {errorMsg}
              </p>
            )}
            {savedAt && !errorMsg && <p className="text-sm text-green-600">Saved.</p>}

            <Button type="submit" disabled={saving} className="cursor-pointer">
              {saving ? "Saving…" : "Save"}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
